import { TFunction } from 'react-i18next';
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { formatDate } from "../../../helpers/dateUtils";
import { formatValue, maybeT } from "../../../helpers/helpers";
import { TransactionDetails, SurchargeItem, DiscountDetails, Payment, TaxRate, ProductType, RefundReason } from "../../../models/transactions";
import { isValidValue, nextY } from "./printCommons";

interface PrintableRow {
  product: string;
  price: number | undefined;
  quantity: number | undefined;
  totalAmount: number;
  productType: ProductType;
  measuredQuantity: number;
  symbol: string;
  barcode: string;
  sku: string;
  notes: string;
  discountAmount: number;
  discountReason: string;
  refundReason?: RefundReason;
  surchargeItem: SurchargeItem;
}

const buildItemRows = (details: TransactionDetails, locale: string, translate: TFunction<"translation", undefined> | undefined): RowInput[] => {
  const rows: RowInput[] = [];

  const pushMain = (
    product: string,
    price: string | number,
    qty: string | number,
    total: string | number
  ) => {
    rows.push([product, price, qty, total]);
  };

  const pushDetail = (left: string, right?: string | number) => {
    rows.push([left, "", "", right ?? ""]);
  };

  const pushSimple = (cells: RowInput) => rows.push(cells);

  const transactionItems = details.transactionItems || [];
  const miscProductItems = details.miscProductItems || [];
  const customerCreditItems = details.customerCreditItems || [];

  const allItems: PrintableRow[] = [
    ...transactionItems.map((it) => ({
      product: it.product,
      price: it.price,
      quantity: it.quantity,
      totalAmount: it.totalAmount,
      productType: it.productType,
      measuredQuantity: it.measuredQuantity,
      symbol: it.symbol,
      barcode: it.barcode,
      sku: it.sku,
      notes: it.notes,
      discountAmount: it.discountAmount,
      discountReason: it.discountReason,
      refundReason: it?.refundReason,
      surchargeItem: it.surchargeItem,
    })),

    ...miscProductItems.map((it) => ({
      product: it.name,
      price: it.price,
      quantity: 1,
      totalAmount: it.price,
      productType: "Standard" as ProductType,
      measuredQuantity: 0,
      symbol: "",
      barcode: "",
      sku: "",
      notes: "",
      discountAmount: 0,
      discountReason: "",
      refundReason: undefined,
      surchargeItem: it.surchargeItem,
    })),

    ...customerCreditItems.map((it) => ({
      product: "Customer Credit",
      price: undefined,
      quantity: undefined,
      totalAmount: it.amount,
      productType: "Standard" as ProductType,
      measuredQuantity: 0,
      symbol: "",
      barcode: "",
      sku: "",
      notes: "",
      discountAmount: 0,
      discountReason: "",
      refundReason: undefined,
      surchargeItem: { amount: 0, taxAmount: 0 },
    })),
  ];

  allItems.forEach((it: PrintableRow) => {
    const priceDisplay =
      it.productType === "Standard" && isValidValue(it.price)
        ? formatValue(it.price, true, details.currency, locale, undefined, true)
        : "";
    const qtyDisplay = it.quantity === undefined || it.quantity === null ? "" : it.quantity;
    const totalDisplay = formatValue(it.totalAmount, true, details.currency, locale, undefined, true);

    pushMain(it.product, priceDisplay, qtyDisplay, totalDisplay);

    if (isValidValue(it.discountAmount) && it.discountAmount !== 0) {
      const reason = isValidValue(it.discountReason) ? it.discountReason : "";
      pushDetail(`Discount: ${reason}`, formatValue(-Math.abs(it.discountAmount), true, details.currency, locale, undefined, true));
    }

    if (isValidValue(it.refundReason)) {
      pushDetail(`${maybeT(translate, "screens.transactions.fields.refundReason", "Refund reason")}: ${it.refundReason?.description}`);
    }

    if ((it.productType === "Weighed" || it.productType === "Measured") && isValidValue(it.measuredQuantity)) {
      const pricePerUnit = isValidValue(it.price)
        ? formatValue(it.price, true, details.currency, locale, 4, true)
        : "";
      pushDetail(`${it.measuredQuantity} ${it.symbol} @ ${pricePerUnit} / ${it.symbol}`);
    }

    if (isValidValue(it.barcode) || isValidValue(it.sku)) {
      const left = isValidValue(it.barcode) ? `Barcode ${it.barcode}` : "";
      const right = isValidValue(it.sku) ? `SKU ${it.sku}` : "";
      pushSimple([left, "", "", right]);
    }

    if (isValidValue(it.notes)) {
      pushSimple(["Notes", "", "", ""]);
      pushSimple([it.notes, "", "", ""]);
    }

    pushSimple(["", "", "", ""]);
  });

  return rows;
};


const getPrintableDetails = (
  options: {
    companyId?: string;
    adminView?: boolean;
    translate?: TFunction<"translation", undefined>;
    locale?: string;
    hasFeatureCCSurcharge?: boolean;
    hasModuleInvoiceNumber?: boolean;
    hasModuleTicketNumber?: boolean;
  } = { locale: "en" },
  ...transactions: TransactionDetails[]
): jsPDF => {
  const { companyId, adminView, translate, locale = "en", hasFeatureCCSurcharge, hasModuleInvoiceNumber, hasModuleTicketNumber } = options;

  // eslint-disable-next-line new-cap
  const doc = new jsPDF();

  transactions.forEach((details, txIndex) => {
    if (txIndex > 0) doc.addPage();

    const generalRows: RowInput[] = [];
    if (adminView && isValidValue(companyId)) generalRows.push([maybeT(translate, "screens.transactions.fields.companyId", "Company ID"), companyId || ""]);
    if (isValidValue(details.date) && details.date !== "TBD") generalRows.push([maybeT(translate, "screens.transactions.fields.time", "Time"), formatDate(details.date, locale)]);
    if (isValidValue(details.staff)) generalRows.push([maybeT(translate, "screens.transactions.fields.staff", "Staff"), details.staff]);
    if (isValidValue(details.location)) generalRows.push([maybeT(translate, "screens.transactions.fields.location", "Location"), details.location]);
    if (isValidValue(details.device)) generalRows.push([maybeT(translate, "screens.transactions.fields.device", "Device"), details.device]);
    if (isValidValue(details.customer)) generalRows.push([maybeT(translate, "screens.transactions.fields.customer", "Customer"), details.customer]);
    if (isValidValue(details.barcode)) generalRows.push([maybeT(translate, "screens.transactions.fields.barcode", "Barcode"), details.barcode]);
    if (isValidValue(details.transactionId)) generalRows.push([maybeT(translate, "screens.transactions.fields.transactionId", "Transaction ID"), details.transactionId]);
    if (hasModuleTicketNumber && isValidValue(details.ticketNumber)) {
      generalRows.push([maybeT(translate, "screens.transactions.fields.ticketNumber", "Ticket Number"), details.ticketNumber || ""]);
    }
    
    if (generalRows.length > 0) {
      autoTable(doc, {
        startY: 10,
        head: [[maybeT(translate, "screens.transactions.fields.field", "Field"), maybeT(translate, "screens.transactions.fields.value", "Value")]],
        body: generalRows,
        theme: "striped",
        columnStyles: { 1: { halign: "right" } },
      });
    }

    if (hasModuleInvoiceNumber && Array.isArray(details.invoiceNumbers) && details.invoiceNumbers.length > 0) {
      const invRows = details.invoiceNumbers.filter(isValidValue).map(i => [i]);
      if (invRows.length > 0) {
        autoTable(doc, {
          startY: nextY(doc),
          head: [[maybeT(translate, "screens.transactions.fields.invoiceNumbers", "Invoice Numbers")]],
          body: invRows,
          theme: "plain",
          columnStyles: { 0: { halign: "right" } },
        });
      }
    }

    const itemRows = buildItemRows(details, locale, translate);
    if (itemRows.length > 0) {
      autoTable(doc, {
        startY: nextY(doc),
        head: [[
          maybeT(translate, "screens.transactions.fields.product", "Product"),
          maybeT(translate, "screens.transactions.fields.price", "Price"),
          maybeT(translate, "screens.transactions.fields.qty", "Qty"),
          maybeT(translate, "screens.transactions.fields.total", "Total"),
        ]],
        body: itemRows,
        theme: "grid",
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
        styles: { cellPadding: 2 },
      });
    }

    const totals: RowInput[] = [];
    if (isValidValue(details.subTotal)) totals.push([maybeT(translate, "screens.transactions.fields.subTotal", "Subtotal"), formatValue(details.subTotal, true, details.currency, locale, undefined, true)]);
    if (hasFeatureCCSurcharge && isValidValue(details.totalSurcharge)) totals.push([maybeT(translate, "screens.transactions.fields.totalSurcharge", "Total Surcharge"), formatValue(details.totalSurcharge, true, details.currency, locale, undefined, true)]);
    if (isValidValue(details.serviceCharge) && details.serviceCharge !== 0) totals.push([maybeT(translate, "screens.transactions.fields.serviceCharge", "Service Charge"), formatValue(details.serviceCharge, true, details.currency, locale, undefined, true)]);
    if (isValidValue(details.gratuity) && details.gratuity !== 0) totals.push([maybeT(translate, "screens.transactions.fields.gratuity", "Gratuity"), formatValue(details.gratuity, true, details.currency, locale, undefined, true)]);
    if (isValidValue(details.giftTreeAmount) && details.giftTreeAmount !== 0) totals.push([maybeT(translate, "screens.transactions.fields.giftTree", "GiftTree Planted"), formatValue(details.giftTreeAmount, true, details.currency, locale, undefined, true)]);

    (details.topLevelDiscountsDetails || []).forEach((d: DiscountDetails) => {
      totals.push([`${maybeT(translate, `screens.transactions.fields.discounts.${d.type}`, "Discount")} (${d.label})`, formatValue(-d.amount, true, details.currency, locale, undefined, true)]);
    });

    if (isValidValue(details.total)) totals.push([maybeT(translate, "screens.transactions.fields.total", "Total"), formatValue(details.total, true, details.currency, locale, undefined, true)]);

    if (totals.length > 0) {
      autoTable(doc, {
        startY: nextY(doc),
        head: [[maybeT(translate, "screens.transactions.fields.summary", "Summary"), maybeT(translate, "screens.transactions.fields.amount", "Amount")]],
        body: totals,
        theme: "striped",
        columnStyles: { 1: { halign: "right" } },
      });
    }

    const paymentRows: RowInput[] = [];

    if (details.payments && details.payments.length > 0) {
      details.payments.forEach((p: Payment) => {
        paymentRows.push([
          `${p.method}${p.lastFourDigits ? ` • ${p.lastFourDigits}` : ""}`,
          formatValue(p.amount || 0, true, details.currency, locale, undefined, true),
        ]);

        const subLeft = p.transactionID ? `# ${p.transactionID}` : "";
        const subRight = (p.date && p.date !== "TBD") ? formatDate(p.date, locale) : "";

        if (subLeft || subRight) {
          paymentRows.push([
            `  ${subLeft}`,
            subRight,
          ]);
        }
      });
    }

    if (isValidValue(details.loyaltyPointsDiscount)) {
      paymentRows.push([
        maybeT(translate, "screens.transactions.fields.loyaltyPoints", "Customer Points"),
        formatValue(details.loyaltyPointsDiscount, true, details.currency, locale, undefined, true),
      ]);
    }

    if (isValidValue(details.change)) {
      paymentRows.push([
        maybeT(translate, "screens.transactions.fields.change", "Change"),
        formatValue(details.change, true, details.currency, locale, undefined, true),
      ]);
    }

    if (paymentRows.length > 0) {
      autoTable(doc, {
        startY: nextY(doc) + 2,         
        head: [[
          maybeT(translate, "screens.transactions.fields.payment", "Payment"),
          maybeT(translate, "screens.transactions.fields.amount", "Amount"),
        ]],
        body: paymentRows,
        theme: "striped",
        columnStyles: { 1: { halign: "right" } },
        styles: { cellPadding: 2 },
        pageBreak: "auto",
      });
    }

    const taxRows: RowInput[] = [];
    (details.taxRates || []).forEach((t: TaxRate) => {
      if (isValidValue(t.name) || isValidValue(t.percentage) || isValidValue(t.amount)) {
        taxRows.push([
          t.name,
          `${formatValue(t.percentage, false, undefined, locale)} %`,
          formatValue(t.amount, true, details.currency, locale, undefined, true),
        ]);
      }
    });

    if (taxRows.length > 0) {
      autoTable(doc, {
        startY: nextY(doc),
        head: [[maybeT(translate, "screens.transactions.fields.taxRate", "Tax Rate"), maybeT(translate, "screens.transactions.fields.percentage", "Percentage"), maybeT(translate, "screens.transactions.fields.tax", "Tax")]],
        body: taxRows,
        theme: "grid",
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
      });
    }

    if (Array.isArray(details.covers) && details.covers.length > 0) {
      const coverRows = details.covers.map((c, idx) => ([`${maybeT(translate, "screens.transactions.fields.cover", "Cover")} ${idx + 1}`, formatValue(c, true, details.currency, locale, undefined, true)]));
      autoTable(doc, {
        startY: nextY(doc),
        head: [[maybeT(translate, "screens.transactions.fields.covers", "Covers"), maybeT(translate, "screens.transactions.fields.value", "Value")]],
        body: coverRows,
        theme: "plain",
        columnStyles: { 1: { halign: "right" } },
      });
    }
  });

  return doc;
};



export default getPrintableDetails;
